// API utility functions for authenticated requests

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://192.168.0.11:3000';

// Get auth token from localStorage
export const getAuthToken = () => {
  return localStorage.getItem('authToken');
};

// Get seller ID from localStorage
export const getSellerId = () => {
  return localStorage.getItem('user');
};

// Get user ID from localStorage
export const getUserId = () => {
  return localStorage.getItem('userId');
};

export const setAuth = (data) => {
  localStorage.setItem("authToken", data.token);
  localStorage.setItem("role", data.role);
  localStorage.setItem("user", JSON.stringify(data.user));
};

// Check if user is authenticated
export const isAuthenticated = () => {
  const token = getAuthToken();
  return token && token !== 'null' && token !== 'undefined';
};

// Clear authentication data
// export const clearAuth = () => {
//   localStorage.removeItem('authToken');
//   localStorage.removeItem('user');
//   localStorage.removeItem('userId');
// };
export const clearAuth = () => {
  localStorage.removeItem("authToken");
  localStorage.removeItem("role");
  localStorage.removeItem("user");
};

// Create authenticated FormData request
export const createAuthenticatedFormData = (data = {}) => {
  const formData = new FormData();
  const token = getAuthToken();

  if (token) {
    formData.append('token', token);
  }

  // Add other data
  Object.keys(data).forEach(key => {
    if (data[key] !== null && data[key] !== undefined) {
      formData.append(key, data[key]);
    }
  });

  return formData;
};

// Make authenticated API request with FormData
export const makeAuthenticatedRequest = async (
  endpoint,
  data = {},
  method = 'POST'
) => {
  try {
    const token = getAuthToken();

    if (!token) {
      throw new Error('No authentication token found');
    }

    let body;

    // ✅ If already FormData → use directly
    if (data instanceof FormData) {
      body = data;
    } else {
      // ✅ Otherwise convert object → FormData
      body = new FormData();
      Object.keys(data).forEach(key => {
        if (data[key] !== null && data[key] !== undefined) {
          body.append(key, data[key]);
        }
      });
    }
    const role = localStorage.getItem("currentRole");

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`, // DO NOT set Content-Type
        "x-role": role,
      },
      body
    });

    const responseData = await response.json();

    if (
      response.status === 401 ||
      responseData?.message?.toLowerCase().includes('token') ||
      responseData?.message?.toLowerCase().includes('unauthorized')
    ) {
      clearAuth();
      // window.location.href = '/login';
      // throw new Error('Authentication failed');
    }

    return responseData;
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
};



// Make authenticated GET request
export const makeAuthenticatedGetRequest = async (endpoint, params = {}) => {
  try {
    const token = getAuthToken();

    if (!token) {
      throw new Error('No authentication token found');
    }

    // Add token to params
    const urlParams = new URLSearchParams({
      token: token,
      ...params
    });

    const response = await fetch(`${API_BASE_URL}${endpoint}?${urlParams}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const responseData = await response.json();

    // Check if token is invalid or expired
    if (responseData.status === 401 || responseData.message?.includes('token') || responseData.message?.includes('unauthorized')) {
      clearAuth();
      // window.location.href = '/login';
      // throw new Error('Authentication failed');
    }

    return responseData;
  } catch (error) {
    console.error('API GET Request Error:', error);
    throw error;
  }
};

// Specific API functions for common operations

// Get offers/bids
export const getOffers = async (page = 1, limit = 10) => {
  return makeAuthenticatedRequest('/api/offers', { page, limit }, 'POST');
};

// Get requests
export const getRequests = async (page = 1, limit = 10, status = 'new') => {
  return makeAuthenticatedRequest('/api/requests', { page, limit, status }, 'POST');
};

// Get offer details
export const getOfferDetails = async (offerId) => {
  return makeAuthenticatedRequest('/api/offer-details', { offer_id: offerId }, 'POST');
};

// Add price to request
export const addPriceToRequest = async (requestId, price, quantity) => {
  return makeAuthenticatedRequest('/api/add-price', {
    request_id: requestId,
    price,
    quantity
  }, 'POST');
};

// Get buyer responses
export const getBuyerResponses = async (offerId) => {
  return makeAuthenticatedRequest('/api/buyer-responses', { offer_id: offerId }, 'POST');
};

// Get dashboard stats
export const getDashboardStats = async () => {
  return makeAuthenticatedRequest('/api/dashboard-stats', {}, 'POST');
};

// Seller Instant Requests (NEW)
export const getSellerInstantRequests = async (
  sellerId,
  page = 1,
  limit = 10,
  filters = {}
) => {
  const payload = {
    seller_id: sellerId,
    page_number: page,
    page_size: limit
  };

  if (filters.grade) payload.grade_id = filters.grade.id;
  if (filters.process) payload.process_id = filters.process.id;
  if (filters.brand) payload.brand_id = filters.brand.id;
  if (filters.grade_code) payload.grade_code_id = filters.grade_code.id;
  if (filters.delivery_location)
    payload.delivery_location_id = filters.delivery_location.id;

  return makeAuthenticatedRequest(
    '/api/seller/instant-request-list',
    payload,
    'POST'
  );
};


// Seller Pending Requests
export const getSellerPendingRequests = async (sellerId, page = 1, limit = 10, filters = {}) => {

  const payload = {
    seller_id: sellerId,
    page_number: page,
    page_size: limit
  };

  if (filters.grade) payload.grade_id = filters.grade.id;
  if (filters.process) payload.process_id = filters.process.id;
  if (filters.brand) payload.brand_id = filters.brand.id;
  if (filters.grade_code) payload.grade_code_id = filters.grade_code.id;
  if (filters.delivery_location)
    payload.delivery_location_id = filters.delivery_location.id;
  return makeAuthenticatedRequest(
    '/api/seller/seller-pending-request-list',
    payload,
    'POST'
  );
};

// Seller Accepted Requests
export const getSellerAcceptedRequests = async (sellerId, page = 1, limit = 10, filters = {}) => {

  const payload = {
    seller_id: sellerId,
    page_number: page,
    page_size: limit
  };

  if (filters.grade) payload.grade_id = filters.grade.id;
  if (filters.process) payload.process_id = filters.process.id;
  if (filters.brand) payload.brand_id = filters.brand.id;
  if (filters.grade_code) payload.grade_code_id = filters.grade_code.id;
  if (filters.delivery_location)
    payload.delivery_location_id = filters.delivery_location.id;
  return makeAuthenticatedRequest(
    '/api/seller/seller-accepted-request-list',
    payload,
    'POST'
  );
};

// Filter list (grade, brand, process, etc.)
export const getIrmFilters = async (filterType) => {
  return makeAuthenticatedRequest(
    '/api/seller/irm-filter-list',
    { filter_type: filterType },
    'POST'
  );
};

// Submit Counter Offer
export const submitCounterOffer = async ({
  sellerId,
  irmRequestId,
  qty,
  price,
  material_location_id,
  offer_availability
}) => {
  // console.log(sellerId, irmRequestId, qty, price, material_location_id, offer_availability);

  return makeAuthenticatedRequest(
    '/api/seller/submit_counter_offer',
    {
      seller_id: sellerId,
      irm_request_id: irmRequestId,
      seller_qty: qty,
      seller_price: price,
      material_location_id: material_location_id,
      offer_availability: offer_availability
    },
    'POST'
  );
};

// Mark IRM request as Not Available
export const markCounterOfferNotAvailable = async (sellerId, irmRequestId) => {
  return makeAuthenticatedRequest(
    '/api/seller/irm-counter-offer-not-available',
    {
      seller_id: sellerId,
      irm_request_id: irmRequestId
    },
    'POST'
  );
};

// Cancel IRM counter offer
export const cancelCounterOffer = async (sellerId, irmSellerOfferId) => {
  return makeAuthenticatedRequest(
    '/api/seller/irm-cancel-counter-offer',
    {
      seller_id: sellerId,
      seller_request_id: irmSellerOfferId
    },
    'POST'
  );
};

// Modify IRM counter offer
export const modifyCounterOffer = async ({
  sellerId,
  irmRequestId,
  qty,
  price,
  material_location_id
}) => {
  return makeAuthenticatedRequest(
    '/api/seller/irm-modify-counter-offer',
    {
      seller_id: sellerId,
      seller_request_id: irmRequestId,
      seller_qty: qty,
      seller_price: price,
      material_location_id
    },
    'POST'
  );
};

// Request call for IRM seller offer
export const requestCallToBuyer = async (sellerId, irmSellerOfferId) => {
  return makeAuthenticatedRequest(
    '/api/seller/call-request-seller',
    {
      seller_id: sellerId,
      irm_seller_offer_id: irmSellerOfferId
    },
    'POST'
  );
};
// Seller notifications
export const getSellerNotifications = async () => {
  const formData = new FormData();
  formData.append('seller_id', localStorage.getItem('user'));
  formData.append('page_size', '10');

  return fetch(`${API_BASE_URL}/api/seller/seller-notifications`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${getAuthToken()}`
    },
    body: formData
  }).then(res => res.json());
};

export const getIrmFilterList = async (filterType) => {


  const formData = new FormData();
  formData.append('filter_type', filterType);
  console.log(formData);

  return makeAuthenticatedRequest(
    '/api/seller/irm-filter-list',
    formData,
    'POST'
  );
};

// ---------------- SELLER OFFERS ----------------

export const getSellerOffers = async ({
  seller_id,
  page_number = 1,
  page_size = 10,
  area_id,
  grade_id
}) => {

  const data = {
    seller_id,
    page_number,
    page_size,
    area_id,
    grade_id
  };

  return makeAuthenticatedRequest(
    "/api/seller/offer-list",
    data
  );
};


export const updateSellerOffers = async (offers) => {

  const token = getAuthToken();
  const role = localStorage.getItem("currentRole");

  const response = await fetch(
    `${API_BASE_URL}/api/seller/post-offers`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "x-role": role,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ offers })
    }
  );

  return response.json();
};

export const createSellerOffer = async (payload) => {
  return makeAuthenticatedRequest(
    "/api/seller/add-offer",
    payload
  );
};


// ---------------- FILTERS ----------------

export const getGradeFilterList = async (seller_id) => {

  return makeAuthenticatedRequest(
    "/api/seller/filter-grade-list",
    { seller_id }
  );

};


export const getMaterialLocationFilterList = async (seller_id) => {

  return makeAuthenticatedRequest(
    "/api/seller/filter-material-location-list",
    { seller_id, search_text: "" }
  );

};

export const getMaterialList = async ({
  seller_id,
  page_number = 1,
  page_size = 10,
  search_text
}) => {

  return makeAuthenticatedRequest(
    "/api/seller/material-list",
    { seller_id, search_text, page_number, page_size }
  );
};
export const getPolymerList = async () => {
  return makeAuthenticatedRequest(
    "/api/seller/polymer-quality-list"
  );
};
export const getPackagingBagList = async () => {
  return makeAuthenticatedRequest(
    "/api/seller/packaging-bag-list"
  );
};
export const getMaterialLocationList = async ({
  page_number = 1,
  page_size = 10,
  search_text
}) => {

  return makeAuthenticatedRequest(
    "/api/seller/material-location-list",
    { search_text, page_number, page_size }
  );
};



export default {
  getAuthToken,
  getSellerId,
  getUserId,
  isAuthenticated,
  clearAuth,
  makeAuthenticatedRequest,
  makeAuthenticatedGetRequest,
  getOffers,
  getRequests,
  getOfferDetails,
  addPriceToRequest,
  getBuyerResponses,
  getDashboardStats,
  getSellerInstantRequests,
  getSellerPendingRequests,
  getSellerAcceptedRequests,
  getIrmFilters,
  submitCounterOffer,
  markCounterOfferNotAvailable,
  cancelCounterOffer,
  modifyCounterOffer,
  requestCallToBuyer,
  getSellerNotifications,
  getIrmFilterList,
  getSellerOffers,
  updateSellerOffers,
  getGradeFilterList,
  getMaterialLocationFilterList,
  getMaterialList,
  getPolymerList,
  getPackagingBagList,
  getMaterialLocationList
};